import { Chip, Divider, Icon, Skeleton } from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";
import { useContext, useMemo, useState } from "react";
import BitumioContext from "providers/BitumioContext";
import { useOpportunityActivities } from "hooks/activities/useOpportunityActivities";
import LogActivityModal from "modules/activities/LogActivityModal/LogActivityModal";
import CreateTaskModal from "modules/tasks/CreateTaskModal/CreateTaskModal";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const ACTIVITY_ICON_MAP: Record<string, string> = {
  CALL: "call",
  TEXT: "sms",
  EMAIL: "email",
  MEETING: "event",
  NOTE: "sticky_note_2",
};

const ACTIVITY_LABEL_MAP: Record<string, string> = {
  CALL: "Call",
  TEXT: "Text Message",
  EMAIL: "Email",
  MEETING: "Meeting",
  NOTE: "Note",
};

function iconForType(type: string) {
  const normalizedType = (type ?? "").toUpperCase();
  return ACTIVITY_ICON_MAP[normalizedType] ?? "bolt";
}

function labelForType(type: string) {
  const normalizedType = (type ?? "").toUpperCase();
  return ACTIVITY_LABEL_MAP[normalizedType] ?? "Activity";
}

function formatOccurredAt(value: string, timezone: string) {
  return dayjs.utc(value).tz(timezone).format("MMM D, YYYY [at] h:mma");
}

export default function OpportunityActivityList({
  contactId,
  companyId,
  opportunityId,
  showHeader = true,
  showLogButton = true,
  onLogActivityClick,
  useInternalLogModal = true,
  opportunityLabel,
}: {
  contactId?: string | null;
  companyId?: string | null;
  opportunityId?: string | null;
  showHeader?: boolean;
  showLogButton?: boolean;
  onLogActivityClick?: () => void;
  useInternalLogModal?: boolean;
  opportunityLabel?: string | null;
}) {
  const { signedInUser } = useContext(BitumioContext);

  const timezone = useMemo(() => {
    return signedInUser.organization?.timezone ?? dayjs.tz.guess() ?? "America/Denver";
  }, [signedInUser]);

  const { activities, loading, error } = useOpportunityActivities({
    contactId,
    opportunityId,
    first: 50,
    page: 1,
  });

  const unified = useMemo(() => {
    const items = activities
      .slice()
      .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
    return items;
  }, [activities]);

  const [open, setOpen] = useState(false);
  const handleLogActivityClick = () => {
    if (onLogActivityClick) {
      onLogActivityClick();
      return;
    }

    setOpen(true);
  };

  const [createTaskOpen, setCreateTaskOpen] = useState(false);

  return (
    <MDBox width="100%">
      {showHeader && (
        <MDBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between">
          <MDTypography variant="h6" fontWeight="medium">
            Activity
          </MDTypography>

          {showLogButton && (
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDButton
                color="blue"
                size="small"
                onClick={handleLogActivityClick}
                disabled={!contactId || !opportunityId}
                startIcon={<Icon>add</Icon>}
              >
                Log Activity
              </MDButton>

              <MDButton
                color="blue"
                size="small"
                onClick={() => setCreateTaskOpen(true)}
                disabled={!opportunityId}
                startIcon={<Icon>add</Icon>}
              >
                Create Task
              </MDButton>
            </MDBox>
          )}
        </MDBox>
      )}

      <MDBox p={2}>
        <MDBox opacity={0.3}>
          <Divider />
        </MDBox>

        {!contactId ? (
          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              No contact selected.
            </MDTypography>
          </MDBox>
        ) : !opportunityId ? (
          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              No opportunity selected.
            </MDTypography>
          </MDBox>
        ) : error ? (
          <MDBox mt={2}>
            <MDAlert color="error">{error.message}</MDAlert>
          </MDBox>
        ) : (
          <MDBox mt={2} sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
            {loading ? (
              <>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </>
            ) : unified.length === 0 ? (
              <MDTypography variant="body2" color="text">
                No activities yet.
              </MDTypography>
            ) : (
              unified.map((a) => (
                <MDBox
                  key={a.id}
                  sx={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small">{iconForType(a.type)}</Icon>
                    <MDTypography variant="body2" fontWeight="medium">
                      {labelForType(a.type)}: {a.summary}
                    </MDTypography>

                    <MDBox flex={1} />
                    <Chip size="small" label="User" color="success" sx={{ fontWeight: 500, height: 20 }} />
                  </MDBox>

                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    {formatOccurredAt(a.occurredAt, timezone)}
                  </MDTypography>

                  {a.body ? (
                    <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                      {a.body}
                    </MDTypography>
                  ) : null}
                </MDBox>
              ))
            )}
          </MDBox>
        )}
      </MDBox>

      {useInternalLogModal && (
        <>
          <LogActivityModal
            open={open}
            onClose={() => setOpen(false)}
            contactId={contactId}
            companyId={companyId}
            opportunityId={opportunityId}
            timezone={timezone}
            requiresOpportunity
          />

          <CreateTaskModal
            open={createTaskOpen}
            onClose={() => setCreateTaskOpen(false)}
            prefillRelated={
              opportunityId
                ? {
                    type: "opportunity",
                    selected: {
                      id: String(opportunityId),
                      label: opportunityLabel || `Opportunity #${String(opportunityId)}`,
                      companyId: companyId ? String(companyId) : null,
                      contactId: contactId ? String(contactId) : null,
                    },
                  }
                : undefined
            }
          />
        </>
      )}
    </MDBox>
  );
}
